import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, X, Loader2 } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { TESTIDS } from "@/constants/testIds";

export default function PublishingTab({ projectId, onRefresh }) {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/publishing/queue", { params: { project_id: projectId } }); setQueue(data); }
    catch { toast.error("Failed to load queue"); }
  }
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [projectId]);

  async function runNow() {
    setBusy(true);
    try {
      const { data } = await api.post("/publishing/run-now");
      toast.success(`Published ${data.published} item${data.published !== 1 ? "s" : ""}`);
      load(); onRefresh?.();
    } catch (err) { toast.error("Run failed"); }
    finally { setBusy(false); }
  }

  async function cancel(id) {
    await api.delete(`/publishing/queue/${id}`); load(); onRefresh?.();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-muted-foreground">
          Drip scheduler runs every 60s. {queue.filter((q) => q.status === "queued").length} item(s) in queue.
        </div>
        <button
          data-testid={TESTIDS.runQueueBtn}
          onClick={runNow} disabled={busy}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50 rounded-sm transition-colors"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run queue now
        </button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid={TESTIDS.queueTable}>
          <thead>
            <tr className="bg-secondary border-b border-border text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Thread</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Service</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Scheduled at</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Status</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">WP</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Retries</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {queue.map((q) => (
              <tr key={q.id} className="border-b border-border last:border-b-0 hover:bg-secondary/40">
                <td className="px-3 py-2 max-w-sm truncate">{q.thread_title || q.thread_id}</td>
                <td className="px-3 py-2 txt-mono">{q.thread_service || "—"}</td>
                <td className="px-3 py-2 txt-mono">{(q.scheduled_at || "").replace("T", " ").slice(0, 19)}</td>
                <td className="px-3 py-2"><StatusPill status={q.status} /></td>
                <td className="px-3 py-2">
                  {q.wp_link ? (
                    <a href={q.wp_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline txt-mono text-[10px]">view ↗</a>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 stat-num">{q.retry_count || 0}</td>
                <td className="px-3 py-2 text-right">
                  {q.status === "queued" && (
                    <button onClick={() => cancel(q.id)} className="text-muted-foreground hover:text-red-400"><X className="w-3 h-3 inline" /></button>
                  )}
                </td>
              </tr>
            ))}
            {queue.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground text-xs">Queue is empty. Schedule threads from the Threads tab.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
